import {
  resolveExperienceFamily,
  sanitizeExperienceId,
} from "./knownExperiences";

const STORAGE_KEY = "guestApp.experience";

const PATH_PREFIXES = [
  ["/beverage-split", "beverage-split"],
  ["/beverage7", "beverage7"],
  ["/beverage6", "beverage6"],
  ["/beverage5", "beverage5"],
  ["/beverage4", "beverage4"],
  ["/beverage3", "beverage3"],
  ["/beverage2", "beverage2"],
  ["/beverage", "beverage"],
  ["/share3/explore", "bubbles3"],
  ["/share2/explore", "bubbles2"],
  ["/share/explore", "bubbles"],
  ["/video/explore", "video"],
  ["/follow/explore", "follow"],
  ["/travel/explore", "visual"],
  ["/interactive/explore", "interactive"],
  ["/derived-visual/explore", "derived-visual"],
  ["/passions-hub", "passions-hub"],
  ["/passion-profile", "passion-profile"],
  ["/consent", "consent"],
  ["/offers", "offers"],
];

const memoryStore = {};

const memoryStorage = {
  getItem(key) {
    return Object.prototype.hasOwnProperty.call(memoryStore, key)
      ? memoryStore[key]
      : null;
  },
  setItem(key, value) {
    memoryStore[key] = String(value);
  },
  removeItem(key) {
    delete memoryStore[key];
  },
};

// Set once a sessionStorage write throws, so later reads use memoryStorage
// instead of returning a stale or missing value.
let sessionStorageFailed = false;

function getDefaultStorage() {
  if (sessionStorageFailed) {
    return memoryStorage;
  }
  try {
    if (typeof sessionStorage !== "undefined") {
      return sessionStorage;
    }
  } catch {
    // fall through to in-memory storage
  }
  return memoryStorage;
}

function normalizePath(pathname = "/") {
  return String(pathname || "/").replace(/\/+$/, "") || "/";
}

export { resolveExperienceFamily, sanitizeExperienceId };

function writeContext(storage, expId, family) {
  if (expId) {
    storage.setItem(STORAGE_KEY, JSON.stringify({ expId, family }));
  } else {
    storage.removeItem(STORAGE_KEY);
  }
}

export function setExperienceContext(experienceId, storage = getDefaultStorage()) {
  const expId = sanitizeExperienceId(experienceId);
  const family = resolveExperienceFamily(expId);
  try {
    writeContext(storage, expId, family);
  } catch {
    // Safari private mode and full quotas throw; fall back to memory so later
    // getExperienceContext() calls see this value.
    try {
      sessionStorageFailed = storage === sessionStorage;
    } catch {
      sessionStorageFailed = true;
    }
    writeContext(memoryStorage, expId, family);
  }
  return { expId: expId || undefined, family };
}

export function getExperienceContext(storage = getDefaultStorage()) {
  try {
    const raw = storage.getItem(STORAGE_KEY);
    if (!raw) {
      return { family: "unknown" };
    }
    const parsed = JSON.parse(raw);
    const expId = sanitizeExperienceId(parsed.expId);
    return {
      expId: expId || undefined,
      family: expId ? resolveExperienceFamily(expId) : "unknown",
    };
  } catch {
    return { family: "unknown" };
  }
}

export function getExperienceParams(storage = getDefaultStorage()) {
  const { expId } = getExperienceContext(storage);
  return expId ? { expId } : {};
}

export function experienceIdFromLocation(pathname = "/", search = "") {
  const query = search.startsWith("?") ? search.slice(1) : search;
  const searchParams = new URLSearchParams(query);
  const fromQuery = sanitizeExperienceId(
    searchParams.get("expId") || searchParams.get("cid"),
  );
  if (fromQuery) {
    return fromQuery;
  }

  const path = normalizePath(pathname);
  for (const [prefix, id] of PATH_PREFIXES) {
    if (path === prefix || path.startsWith(`${prefix}/`)) {
      return id;
    }
  }
  return "";
}

export function applyExperienceFromLocation(
  pathname,
  search,
  storage = getDefaultStorage(),
) {
  const expId = experienceIdFromLocation(pathname, search);
  if (expId) {
    return setExperienceContext(expId, storage);
  }
  return getExperienceContext(storage);
}

export function syncExperienceContextFromLocation(locationLike = window.location) {
  const { pathname, search, hash } = locationLike;
  let path = pathname || "/";
  let query = search || "";

  if (hash && hash.length > 1) {
    const withoutHash = hash.replace(/^#/, "");
    const [hashPath, hashQuery] = withoutHash.split("?");
    if (hashPath) {
      path = hashPath;
    }
    if (hashQuery) {
      query = `?${hashQuery}`;
    }
  }

  return applyExperienceFromLocation(path, query);
}
